import React, { useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import Button from '../../components/common/Button';
import '../../styles/signup-page.css';

const tiers: Record<
  string,
  { name: string; price: number; users: string; features: string[] }
> = {
  starter: {
    name: 'Starter',
    price: 129,
    users: '1\u20139 users',
    features: ['AI-powered reports', 'Real-time monitoring', 'Mobile app access', 'Email support'],
  },
  growth: {
    name: 'Growth',
    price: 249,
    users: '10\u201324 users',
    features: ['Everything in Starter', 'Priority support', 'Custom report templates', 'Analytics dashboard'],
  },
  professional: {
    name: 'Professional',
    price: 449,
    users: '25\u201349 users',
    features: ['Everything in Growth', 'Advanced analytics', 'API access', 'Dedicated support'],
  },
  business: {
    name: 'Business',
    price: 749,
    users: '50\u201399 users',
    features: ['Everything in Professional', 'Custom integrations', 'SLA guarantee', 'Account manager'],
  },
  enterprise: {
    name: 'Enterprise',
    price: 1249,
    users: '100\u2013199 users',
    features: ['Everything in Business', 'White-label options', 'Custom development', 'On-site training'],
  },
};

// Set to true when Stripe is configured and ready to accept payments
const BILLING_ENABLED = false;

const SignupPage: React.FC = () => {
  const { tier } = useParams<{ tier: string }>();
  const tierData = tier ? tiers[tier] : undefined;

  const [companyName, setCompanyName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Redirect to pricing if the tier is invalid
  if (!tierData) {
    return <Navigate to="/pricing" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!agreedToTerms) {
      setError('Please agree to the Terms of Service and Privacy Policy.');
      setLoading(false);
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://d3t71xc0hyi6iy.cloudfront.net/api';
      const response = await fetch(`${apiUrl}/billing/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tier: tier,
          companyName,
          adminEmail: email,
          adminFirstName: firstName,
          adminLastName: lastName,
        }),
      });

      const data = await response.json();
      if (data.sessionUrl) {
        window.location.href = data.sessionUrl;
      } else {
        setError('Failed to create checkout session. Please try again.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-page">
      {/* Hero */}
      <section className="signup-hero">
        <div className="container">
          <div className="signup-hero__content fade-in-up">
            <h1 className="signup-hero__headline">
              Get Started with {tierData.name}
            </h1>
            <p className="signup-hero__sub">
              Fill out your details below and you'll be redirected to secure
              checkout.
            </p>
          </div>
        </div>
      </section>

      {/* Form + Plan Summary */}
      <section className="signup-main">
        <div className="container">
          <div className="signup-main__grid fade-in-up">
            {/* Form Card */}
            <div className="signup-form-card">
              <h2 className="signup-form-card__title">Account Details</h2>
              <p className="signup-form-card__subtitle">
                Enter your information to create your account.
              </p>

              {error && (
                <div className="signup-alert signup-alert--error">{error}</div>
              )}

              <form className="signup-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label" htmlFor="companyName">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    className="form-input"
                    placeholder="Your company name"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    required
                  />
                </div>

                <div className="signup-form__row">
                  <div className="form-group">
                    <label className="form-label" htmlFor="firstName">
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      className="form-input"
                      placeholder="John"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="lastName">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      className="form-input"
                      placeholder="Smith"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="email">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="form-input"
                    placeholder="john@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="signup-form__checkbox-group">
                  <input
                    type="checkbox"
                    id="terms"
                    className="signup-form__checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                  />
                  <label htmlFor="terms" className="signup-form__checkbox-label">
                    I agree to the{' '}
                    <Link to="/terms" target="_blank">
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy" target="_blank">
                      Privacy Policy
                    </Link>
                  </label>
                </div>

                <Button
                  variant="primary"
                  size="lg"
                  type="submit"
                  style={{ width: '100%', ...(!BILLING_ENABLED && { opacity: 0.5, cursor: 'not-allowed' }) }}
                  loading={loading}
                  disabled={!BILLING_ENABLED || loading}
                >
                  {loading ? 'Processing...' : BILLING_ENABLED ? 'Continue to Payment' : 'Coming Soon'}
                </Button>
                {!BILLING_ENABLED && (
                  <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.875rem', marginTop: '0.75rem' }}>
                    Online signup is coming soon. <a href="/contact">Contact us</a> to get started.
                  </p>
                )}
              </form>
            </div>

            {/* Plan Summary Sidebar */}
            <div className="signup-summary">
              <div className="signup-summary-card">
                <div className="signup-summary-card__header">
                  <p className="signup-summary-card__label">Selected Plan</p>
                  <h3 className="signup-summary-card__tier">{tierData.name}</h3>
                  <div className="signup-summary-card__price">
                    <span className="signup-summary-card__price-amount">
                      ${tierData.price}
                    </span>
                    <span className="signup-summary-card__price-period">/mo</span>
                  </div>
                  <p className="signup-summary-card__users">{tierData.users}</p>
                </div>
                <ul className="signup-summary-card__features">
                  {tierData.features.map((feature) => (
                    <li key={feature} className="signup-summary-card__feature">
                      <span className="signup-summary-card__check">
                        {'\u2713'}
                      </span>
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to="/pricing" className="signup-summary-card__change">
                  {'\u2190'} Change plan
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SignupPage;
